import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Typography, Paper, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle,
  CircularProgress, Alert, Box, Chip, IconButton, Tooltip, Grid, Card, CardContent,
  Divider, Stack, InputAdornment, Tab, Tabs
} from '@mui/material';
import {
  Check, Close, Refresh, Business, DirectionsCar, Description, Search,
  Visibility, Room, HelpOutline, Info, History, ZoomIn, PhotoLibrary
} from '@mui/icons-material';
import { getImageUrl } from '../utils/imageUrl';

/* Clickable document image tile with lightbox preview */
const DocImage = ({ src, label, onZoom }) => {
  if (!src) return (
    <Box sx={{ width: 130, height: 90, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px dashed #cbd5e1' }}>
      <Description sx={{ color: '#94a3b8', fontSize: 24 }} />
      <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>{label}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Missing</Typography>
    </Box>
  );

  const fullUrl = getImageUrl(src);

  return (
    <Tooltip title={`Click to zoom ${label}`}>
      <Box
        sx={{ position: 'relative', cursor: 'pointer', width: 130, '&:hover .zoom-overlay': { opacity: 1 } }}
        onClick={() => onZoom(fullUrl, label)}
      >
        <Box
          component="img"
          src={fullUrl}
          alt={label}
          sx={{
            width: 130, height: 90, objectFit: 'cover', borderRadius: 2,
            border: '1px solid #cbd5e1', display: 'block', transition: 'all 0.2s'
          }}
        />
        <Box className="zoom-overlay" sx={{
          position: 'absolute', top: 0, left: 0, width: 130, height: 90, borderRadius: 2,
          backgroundColor: 'rgba(0,0,0,0.4)', color: 'white', display: 'flex',
          alignItems: 'center', justifyContent: 'center', opacity: 0, transition: '0.2s opacity'
        }}>
          <ZoomIn />
        </Box>
        <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 0.5, color: '#64748b', fontWeight: 600 }} noWrap>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const VehicleApproval = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState('all');

  // Modal & Dialog state
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' | 'reject' | 'request_info'
  const [inputReason, setInputReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);

  const fetchPendingVehicles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/admin/pending-vehicles');
      setVehicles(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch pending vehicles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingVehicles();
  }, [fetchPendingVehicles]);

  const handleOpenAction = (vehicle, type) => {
    setSelectedVehicle(vehicle);
    setActionType(type);
    setInputReason('');
    setActionDialogOpen(true);
  };

  const handleExecuteAction = async () => {
    if (!selectedVehicle) return;
    setProcessing(true);
    setError('');
    setSuccessMsg('');

    try {
      if (actionType === 'approve') {
        await axios.put(`/admin/approve-vehicle/${selectedVehicle._id}`, { note: inputReason });
        setSuccessMsg(`Vehicle ${selectedVehicle.vehicleNumber} approved successfully.`);
      } else if (actionType === 'reject') {
        if (!inputReason.trim()) {
          setError('Rejection reason is required.');
          setProcessing(false);
          return;
        }
        await axios.put(`/admin/reject-vehicle/${selectedVehicle._id}`, { reason: inputReason });
        setSuccessMsg(`Vehicle ${selectedVehicle.vehicleNumber} rejected.`);
      } else if (actionType === 'request_info') {
        if (!inputReason.trim()) {
          setError('Details are required when requesting information.');
          setProcessing(false);
          return;
        }
        await axios.put(`/admin/request-info-vehicle/${selectedVehicle._id}`, { details: inputReason });
        setSuccessMsg(`Information request sent for vehicle ${selectedVehicle.vehicleNumber}.`);
      }

      setActionDialogOpen(false);
      setOpenDetailModal(false);
      fetchPendingVehicles();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process vehicle action.');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusChip = (v) => {
    const status = v.verificationStatus || 'pending';
    switch (status) {
      case 'approved':
        return <Chip label="Approved" color="success" size="small" icon={<Check />} />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" icon={<Close />} />;
      case 'info_requested':
        return <Chip label="Info Requested" color="warning" size="small" icon={<HelpOutline />} />;
      default:
        return <Chip label="Pending Review" color="info" size="small" icon={<Info />} />;
    }
  };

  const filteredVehicles = vehicles.filter((v) => {
    const status = v.verificationStatus || 'pending';
    const matchesTab = filterTab === 'all' ? true : status === filterTab;
    const matchesSearch =
      v.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.area?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.ownerId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.ownerId?.businessName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <Box sx={{ p: 1 }}>
      {/* Header Banner */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f172a' }}>
            Vehicle Approval Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review commercial fleet vehicles, owner assignments, RC documents, insurance, and execute verification actions.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchPendingVehicles} disabled={loading}>
          Refresh List
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Filter Tabs & Search Bar */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Tabs
              value={filterTab}
              onChange={(e, val) => setFilterTab(val)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
            >
              <Tab label={`All (${vehicles.length})`} value="all" />
              <Tab label={`Pending (${vehicles.filter(v => !v.verificationStatus || v.verificationStatus === 'pending').length})`} value="pending" />
              <Tab label={`Info Requested (${vehicles.filter(v => v.verificationStatus === 'info_requested').length})`} value="info_requested" />
              <Tab label={`Rejected (${vehicles.filter(v => v.verificationStatus === 'rejected').length})`} value="rejected" />
            </Tabs>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search vehicle number, owner, area, model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Vehicle Cards Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredVehicles.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DirectionsCar sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No Vehicles Found</Typography>
          <Typography variant="body2" color="text.disabled">
            {searchTerm ? 'No vehicles matching your search criteria.' : 'All vehicle verification requests have been processed.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredVehicles.map((vehicle) => (
            <Grid item xs={12} key={vehicle._id}>
              <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Box sx={{ height: 4, bgcolor: vehicle.verificationStatus === 'info_requested' ? '#f59e0b' : vehicle.verificationStatus === 'rejected' ? '#ef4444' : '#0284c7' }} />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {/* Vehicle Header & Specs */}
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Chip label={vehicle.vehicleNumber} color="primary" sx={{ fontWeight: 'bold', fontSize: '1rem', py: 0.5 }} />
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1, textTransform: 'capitalize' }}>
                            {vehicle.type} — {vehicle.model || 'Standard'}
                          </Typography>
                        </Box>
                        {getStatusChip(vehicle)}
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={0.8}>
                        <Typography variant="body2"><strong>Tank Capacity:</strong> {vehicle.tankCapacity} L</Typography>
                        <Typography variant="body2"><strong>Base Rate:</strong> ₹{vehicle.rate || '—'}</Typography>
                        {vehicle.pricePer1000 > 0 && <Typography variant="body2"><strong>Per 1000L:</strong> ₹{vehicle.pricePer1000}</Typography>}
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Room fontSize="small" color="action" /> {vehicle.area || 'Default Area'}
                        </Typography>
                      </Stack>
                    </Grid>

                    {/* Owner & Assigned Driver Info */}
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Business fontSize="small" /> Owner & Operator Info
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Stack spacing={0.8}>
                          <Typography variant="body2">
                            <strong>Owner:</strong> {vehicle.ownerId?.name || 'N/A'}
                          </Typography>
                          {vehicle.ownerId?.businessName && (
                            <Typography variant="body2">
                              <strong>Business:</strong> {vehicle.ownerId.businessName}
                            </Typography>
                          )}
                          {vehicle.ownerId?.phoneNumber && (
                            <Typography variant="body2">
                              <strong>Owner Contact:</strong> {vehicle.ownerId.phoneNumber}
                            </Typography>
                          )}
                          {vehicle.driverId?.name && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Assigned Driver:</strong> {vehicle.driverId.name} ({vehicle.driverId.phoneNumber})
                            </Typography>
                          )}
                        </Stack>
                      </Paper>

                      {vehicle.requestedInfoNote && (
                        <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                          <strong>Info Requested:</strong> {vehicle.requestedInfoNote}
                        </Alert>
                      )}
                      {vehicle.rejectionReason && (
                        <Alert severity="error" sx={{ mt: 1.5, py: 0.5, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                          <strong>Rejection Reason:</strong> {vehicle.rejectionReason}
                        </Alert>
                      )}
                    </Grid>

                    {/* Vehicle Photos & Docs */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhotoLibrary fontSize="small" /> Vehicle Photos & RC/Insurance
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <DocImage src={vehicle.photoFront} label="Front Photo" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={vehicle.photoBack} label="Back Photo" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={vehicle.photoSide} label="Side Photo" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={vehicle.vehicleRC} label="RC Book" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={vehicle.vehicleInsurance} label="Insurance Doc" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Actions Footer */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => { setSelectedVehicle(vehicle); setOpenDetailModal(true); }}
                    >
                      Full Details & History
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Check />}
                      onClick={() => handleOpenAction(vehicle, 'approve')}
                    >
                      Approve Vehicle
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<HelpOutline />}
                      onClick={() => handleOpenAction(vehicle, 'request_info')}
                    >
                      Request Info
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={() => handleOpenAction(vehicle, 'reject')}
                    >
                      Reject Vehicle
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Full Vehicle Profile & Audit Log Modal */}
      {selectedVehicle && (
        <Dialog open={openDetailModal} onClose={() => setOpenDetailModal(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip label={selectedVehicle.vehicleNumber} color="primary" sx={{ fontWeight: 'bold' }} />
              <Typography variant="h6" fontWeight="bold">{selectedVehicle.type?.toUpperCase()} - {selectedVehicle.model}</Typography>
            </Box>
            {getStatusChip(selectedVehicle)}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>Technical & Pricing Specifications</Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2"><b>Registration No:</b> {selectedVehicle.vehicleNumber}</Typography>
                    <Typography variant="body2"><b>Vehicle Type:</b> {selectedVehicle.type}</Typography>
                    <Typography variant="body2"><b>Model:</b> {selectedVehicle.model}</Typography>
                    <Typography variant="body2"><b>Tank Capacity:</b> {selectedVehicle.tankCapacity} Liters</Typography>
                    <Typography variant="body2"><b>Base Service Rate:</b> ₹{selectedVehicle.rate}</Typography>
                    <Typography variant="body2"><b>Price / 1000L:</b> ₹{selectedVehicle.pricePer1000 || 0}</Typography>
                    <Typography variant="body2"><b>Operating Area / Zone:</b> {selectedVehicle.area}</Typography>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>Ownership & Driver Information</Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2"><b>Owner Name:</b> {selectedVehicle.ownerId?.name || 'N/A'}</Typography>
                    <Typography variant="body2"><b>Business Name:</b> {selectedVehicle.ownerId?.businessName || 'N/A'}</Typography>
                    <Typography variant="body2"><b>Owner Phone:</b> {selectedVehicle.ownerId?.phoneNumber || 'N/A'}</Typography>
                    <Typography variant="body2"><b>Driver Name:</b> {selectedVehicle.driverId?.name || 'Unassigned'}</Typography>
                    <Typography variant="body2"><b>Driver Phone:</b> {selectedVehicle.driverId?.phoneNumber || 'N/A'}</Typography>
                  </Stack>
                </Paper>
              </Grid>

              {/* Photos & Docs */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>All Uploaded Photos & Documents</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <DocImage src={selectedVehicle.photoFront} label="Front Photo" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={selectedVehicle.photoBack} label="Back Photo" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={selectedVehicle.photoSide} label="Side Photo" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={selectedVehicle.vehicleRC} label="RC Book" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={selectedVehicle.vehicleInsurance} label="Insurance Doc" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                </Box>
              </Grid>

              {/* History Log */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <History fontSize="small" /> Verification Audit Log
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                  {selectedVehicle.approvalHistory && selectedVehicle.approvalHistory.length > 0 ? (
                    <Stack spacing={1.5}>
                      {selectedVehicle.approvalHistory.map((item, idx) => (
                        <Box key={idx} sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, borderLeft: '4px solid #0284c7' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                              Action: {item.action?.replace('_', ' ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{new Date(item.timestamp).toLocaleString()}</Typography>
                          </Box>
                          {item.reason && <Typography variant="body2" color="text.secondary">Note/Reason: {item.reason}</Typography>}
                          {item.requestedDetails && <Typography variant="body2" color="warning.main">Requested Details: {item.requestedDetails}</Typography>}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No prior approval history logged. Initial vehicle review.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, backgroundColor: '#f1f5f9' }}>
            <Button onClick={() => setOpenDetailModal(false)} color="inherit">Close</Button>
            <Button variant="outlined" color="warning" startIcon={<HelpOutline />} onClick={() => handleOpenAction(selectedVehicle, 'request_info')}>
              Request Information
            </Button>
            <Button variant="outlined" color="error" startIcon={<Close />} onClick={() => handleOpenAction(selectedVehicle, 'reject')}>
              Reject Vehicle
            </Button>
            <Button variant="contained" color="success" startIcon={<Check />} onClick={() => handleOpenAction(selectedVehicle, 'approve')}>
              Approve Vehicle
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Action Dialog (Approve / Reject / Request Info) */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' && 'Approve Vehicle Registration'}
          {actionType === 'reject' && 'Reject Vehicle Application'}
          {actionType === 'request_info' && 'Request Additional Vehicle Information'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vehicle: <strong>{selectedVehicle?.vehicleNumber}</strong> ({selectedVehicle?.type} - {selectedVehicle?.model})
          </Typography>

          {actionType === 'approve' && (
            <TextField
              fullWidth
              label="Approval Note (Optional)"
              placeholder="e.g. RC and insurance verified successfully"
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
            />
          )}

          {actionType === 'reject' && (
            <TextField
              fullWidth
              required
              label="Rejection Reason"
              placeholder="e.g. Expired RC document or unreadable insurance copy"
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
              error={!inputReason.trim()}
              helperText="State the reason for rejecting this vehicle."
            />
          )}

          {actionType === 'request_info' && (
            <TextField
              fullWidth
              required
              label="Requested Information & Instructions"
              placeholder="e.g. Please upload a clear photo of the side of the vehicle displaying the tank capacity."
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
              error={!inputReason.trim()}
              helperText="Specify what information or photos must be resubmitted."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setActionDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === 'approve' ? 'success' : actionType === 'reject' ? 'error' : 'warning'}
            onClick={handleExecuteAction}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={18} /> : actionType === 'approve' ? <Check /> : actionType === 'reject' ? <Close /> : <HelpOutline />}
          >
            Confirm {actionType.replace('_', ' ')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox Zoom Modal */}
      {zoomImage && (
        <Dialog open={Boolean(zoomImage)} onClose={() => setZoomImage(null)} maxWidth="lg">
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {zoomImage.title}
            <IconButton onClick={() => setZoomImage(null)}><Close /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', p: 1 }}>
            <img src={zoomImage.url} alt={zoomImage.title} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default VehicleApproval;
